import { api } from "../../../api/Middleware";
import {
  MARKETPLACE_BANNERS_URL,
  MARKETPLACE_BANNERS_TOGGLE_ACTIVE_URL,
  MARKETPLACE_BANNERS_DUPLICATE_URL,
  MARKETPLACE_BANNERS_BULK_UPDATE_ORDER_URL,
  MARKETPLACE_BANNERS_STATS_URL,
  MARKETPLACE_BANNERS_TRACK_VIEW_URL,
  MARKETPLACE_BANNERS_TRACK_CLICK_URL,
  MARKETPLACE_BANNERS_TRACK_METRICS_URL,
} from "../../../api/Urls";

// =============================
// MARKETPLACE BANNERS SERVICES
// =============================

class MarketplaceBannersServices {
  // =============================
  // CRUD OPERATIONS
  // =============================

  // Listar todos os banners
  static async listBanners(params = {}) {
    try {
      const queryString = Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
        
      const url = queryString ? `${MARKETPLACE_BANNERS_URL}?${queryString}` : MARKETPLACE_BANNERS_URL;
      return await api.get(url);
    } catch (error) {
      console.error('Erro ao buscar banners do marketplace:', error);
      throw error;
    }
  }

  // Buscar banner específico
  static async getBanner(id) {
    try {
      return await api.get(`${MARKETPLACE_BANNERS_URL}${id}/`);
    } catch (error) {
      console.error('Erro ao buscar banner do marketplace:', error);
      throw error;
    }
  }

  // Criar novo banner
  static async createBanner(data) {
    try {
      return await api.post(MARKETPLACE_BANNERS_URL, data);
    } catch (error) {
      console.error('Erro ao criar banner no marketplace:', error);
      throw error;
    }
  }

  // Criar novo banner com arquivo
  static async createBannerWithFile(formData) {
    try {
      return await api.post(MARKETPLACE_BANNERS_URL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      console.error('Erro ao criar banner com arquivo no marketplace:', error);
      throw error;
    }
  }

  // Atualizar banner existente (PUT)
  static async updateBanner(id, data) {
    try {
      return await api.put(`${MARKETPLACE_BANNERS_URL}${id}/`, data);
    } catch (error) {
      console.error('Erro ao atualizar banner do marketplace:', error);
      throw error;
    }
  }

  // Atualizar banner existente com arquivo
  static async updateBannerWithFile(id, formData) {
    try {
      return await api.put(`${MARKETPLACE_BANNERS_URL}${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      console.error('Erro ao atualizar banner com arquivo no marketplace:', error);
      throw error;
    }
  }

  // Atualizar parcialmente banner (PATCH)
  static async patchBanner(id, data) {
    try {
      return await api.patch(`${MARKETPLACE_BANNERS_URL}${id}/`, data);
    } catch (error) {
      console.error('Erro ao atualizar banner do marketplace:', error);
      throw error;
    }
  }

  // Deletar banner
  static async deleteBanner(id) {
    try {
      return await api.delete(`${MARKETPLACE_BANNERS_URL}${id}/`);
    } catch (error) {
      console.error('Erro ao deletar banner do marketplace:', error);
      throw error;
    }
  }

  // =============================
  // BANNER ACTIONS
  // =============================

  // Alternar status ativo/inativo
  static async toggleActive(id) {
    try {
      return await api.post(MARKETPLACE_BANNERS_TOGGLE_ACTIVE_URL(id));
    } catch (error) {
      console.error('Erro ao alternar status do banner:', error);
      throw error;
    }
  }

  // Duplicar banner
  static async duplicateBanner(id) {
    try {
      return await api.post(MARKETPLACE_BANNERS_DUPLICATE_URL(id));
    } catch (error) {
      console.error('Erro ao duplicar banner:', error);
      throw error;
    }
  }

  // Atualizar ordem em lote
  static async bulkUpdateOrder(banners) {
    try {
      return await api.post(MARKETPLACE_BANNERS_BULK_UPDATE_ORDER_URL, { banners });
    } catch (error) {
      console.error('Erro ao atualizar ordem dos banners:', error);
      throw error;
    }
  }

  // Obter estatísticas dos banners
  static async getBannerStats() {
    try {
      return await api.get(MARKETPLACE_BANNERS_STATS_URL);
    } catch (error) {
      console.error('Erro ao buscar estatísticas dos banners:', error);
      throw error;
    }
  }

  // =============================
  // METRICS TRACKING
  // =============================

  // Registrar visualização
  static async trackView(id) {
    try {
      return await api.post(MARKETPLACE_BANNERS_TRACK_VIEW_URL(id));
    } catch (error) {
      console.error('Erro ao registrar visualização:', error);
      throw error;
    }
  }

  // Registrar clique
  static async trackClick(id) {
    try {
      return await api.post(MARKETPLACE_BANNERS_TRACK_CLICK_URL(id));
    } catch (error) {
      console.error('Erro ao registrar clique:', error);
      throw error;
    }
  }

  // Registrar métricas (alternativo)
  static async trackMetrics(bannerId, action) {
    try {
      return await api.post(MARKETPLACE_BANNERS_TRACK_METRICS_URL, {
        banner_id: bannerId,
        action: action // 'view' ou 'click'
      });
    } catch (error) {
      console.error('Erro ao registrar métrica:', error);
      throw error;
    }
  }

  // =============================
  // HELPER FUNCTIONS
  // =============================

  // Função auxiliar para construir parâmetros de busca
  static buildSearchParams(filters = {}) {
    const params = {};
    
    // Filtros básicos
    if (filters.is_active !== undefined && filters.is_active !== 'all') {
      params.is_active = filters.is_active === 'true';
    }
    
    if (filters.search && filters.search.trim()) {
      params.search = filters.search.trim();
    }
    
    if (filters.created_by && filters.created_by !== 'all') {
      params.created_by = filters.created_by;
    }
    
    // Paginação
    if (filters.page) {
      params.page = filters.page;
    }
    
    if (filters.page_size) {
      params.page_size = filters.page_size;
    }
    
    // Ordenação
    if (filters.ordering) {
      params.ordering = filters.ordering;
    }
    
    return params;
  }

  // Função auxiliar para formatar dados do banner
  static formatBannerData(banner) {
    if (!banner) return null;

    return {
      ...banner,
      formattedCreatedAt: banner.created_at 
        ? new Date(banner.created_at).toLocaleDateString('pt-BR')
        : '',
      formattedUpdatedAt: banner.updated_at
        ? new Date(banner.updated_at).toLocaleDateString('pt-BR')
        : '',
      formattedStartDate: banner.start_date
        ? new Date(banner.start_date).toLocaleDateString('pt-BR')
        : null,
      formattedEndDate: banner.end_date
        ? new Date(banner.end_date).toLocaleDateString('pt-BR')
        : null,
      statusVariant: MarketplaceBannersServices.getStatusVariant(banner.is_active, banner.is_visible),
      statusText: MarketplaceBannersServices.getStatusText(banner.is_active, banner.is_visible),
      canEdit: true, // Banners sempre podem ser editados
      canToggle: true, // Status sempre pode ser alterado
      canDuplicate: true, // Banners sempre podem ser duplicados
      canDelete: true, // Banners sempre podem ser deletados
      clickThroughRate: banner.view_count > 0 ? ((banner.click_count / banner.view_count) * 100).toFixed(2) : 0,
    };
  }

  // Função auxiliar para obter variante de cor do status
  static getStatusVariant(isActive, isVisible) {
    if (!isActive) return "secondary";
    if (isVisible) return "success";
    return "warning";
  }

  // Função auxiliar para obter texto do status
  static getStatusText(isActive, isVisible) {
    if (!isActive) return "Inativo";
    if (isVisible) return "Ativo e Visível";
    return "Ativo (Agendado)";
  }

  // Validar dados para criação de banner
  static validateBannerData(data) {
    const errors = [];
    
    if (!data.title || !data.title.trim()) {
      errors.push("Título é obrigatório");
    } else if (data.title.length > 200) {
      errors.push("Título deve ter no máximo 200 caracteres");
    }

    if (data.subtitle && data.subtitle.length > 300) {
      errors.push("Subtítulo deve ter no máximo 300 caracteres");
    }

    if (data.button_text && data.button_text.length > 100) {
      errors.push("Texto do botão deve ter no máximo 100 caracteres");
    }

    if (data.button_text && (!data.button_link || !data.button_link.trim())) {
      errors.push("Link do botão é obrigatório quando texto do botão é fornecido");
    }

    if (data.button_link && !MarketplaceBannersServices.isValidUrl(data.button_link)) {
      errors.push("Link do botão deve ser uma URL válida");
    }

    if (data.start_date && data.end_date) {
      const startDate = new Date(data.start_date);
      const endDate = new Date(data.end_date);
      if (endDate <= startDate) {
        errors.push("Data de fim deve ser posterior à data de início");
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Verificar se URL é válida
  static isValidUrl(string) {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  }

  // Preparar dados para criação/atualização de banner
  static prepareBannerData(formData) {
    const data = {
      title: formData.title.trim(),
    };
    
    if (formData.subtitle && formData.subtitle.trim()) {
      data.subtitle = formData.subtitle.trim();
    }

    if (formData.button_text && formData.button_text.trim()) {
      data.button_text = formData.button_text.trim();
    }

    if (formData.button_link && formData.button_link.trim()) {
      data.button_link = formData.button_link.trim();
    }

    if (formData.is_active !== undefined) {
      data.is_active = formData.is_active;
    }

    if (formData.sort_order !== undefined) {
      data.sort_order = parseInt(formData.sort_order) || 0;
    }

    if (formData.start_date) {
      data.start_date = formData.start_date;
    }

    if (formData.end_date) {
      data.end_date = formData.end_date;
    }

    if (formData.image) {
      data.image = formData.image;
    }
    
    return data;
  }

  // Função para buscar banners com filtros avançados
  static async searchBanners(searchOptions = {}) {
    try {
      const {
        query = '',
        isActive = 'all',
        createdBy = 'all',
        dateFrom = null,
        dateTo = null,
        sortBy = 'sort_order',
        sortOrder = 'asc',
        page = 1,
        pageSize = 10
      } = searchOptions;

      const params = {
        page,
        page_size: pageSize,
      };

      if (query.trim()) {
        params.search = query.trim();
      }

      if (isActive && isActive !== 'all') {
        params.is_active = isActive === 'true';
      }

      if (createdBy && createdBy !== 'all') {
        params.created_by = createdBy;
      }

      if (dateFrom) {
        params.created_after = dateFrom;
      }

      if (dateTo) {
        params.created_before = dateTo;
      }

      if (sortBy) {
        params.ordering = sortOrder === 'desc' ? `-${sortBy}` : sortBy;
      }

      return await MarketplaceBannersServices.listBanners(params);
    } catch (error) {
      console.error('Erro na busca avançada de banners:', error);
      throw error;
    }
  }

  // Função para exportar dados dos banners
  static async exportBanners(format = 'json', filters = {}) {
    try {
      const params = {
        ...MarketplaceBannersServices.buildSearchParams(filters),
        export_format: format,
        page_size: 1000, // Limite alto para exportação
      };

      const response = await MarketplaceBannersServices.listBanners(params);
      const data = response.data.results || [];

      if (format === 'csv') {
        return MarketplaceBannersServices.convertToCSV(data);
      }

      return data;
    } catch (error) {
      console.error('Erro ao exportar banners:', error);
      throw error;
    }
  }

  // Converter dados para CSV
  static convertToCSV(data) {
    if (!data || data.length === 0) {
      return '';
    }

    const headers = [
      'ID',
      'Título',
      'Subtítulo',
      'Texto do Botão',
      'Link do Botão',
      'Status',
      'Ordem',
      'Visualizações',
      'Cliques',
      'CTR (%)',
      'Início',
      'Fim',
      'Criado por',
      'Criado em',
      'Atualizado em'
    ];

    const rows = data.map(banner => [
      banner.id,
      banner.title || '',
      banner.subtitle || '',
      banner.button_text || '',
      banner.button_link || '',
      banner.is_active ? (banner.is_visible ? 'Ativo e Visível' : 'Ativo (Agendado)') : 'Inativo',
      banner.sort_order || 0,
      banner.view_count || 0,
      banner.click_count || 0,
      banner.click_through_rate || 0,
      banner.start_date ? new Date(banner.start_date).toLocaleDateString('pt-BR') : '',
      banner.end_date ? new Date(banner.end_date).toLocaleDateString('pt-BR') : '',
      banner.created_by_name || '',
      new Date(banner.created_at).toLocaleDateString('pt-BR'),
      new Date(banner.updated_at).toLocaleDateString('pt-BR')
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    return csvContent;
  }

  // Download do arquivo CSV
  static downloadCSV(csvContent, filename = 'marketplace-banners.csv') {
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // =============================
  // UPLOAD HELPERS
  // =============================

  // Converter arquivo para base64
  static fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }

  // Validar formato de imagem
  static validateImageFile(file, maxSizeMB = 5) {
    const errors = [];
    
    if (!file) {
      errors.push("Arquivo é obrigatório");
      return { isValid: false, errors };
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      errors.push("Formato de arquivo inválido. Apenas JPG, PNG, WebP e GIF são aceitos.");
    }

    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      errors.push(`Arquivo muito grande. Tamanho máximo: ${maxSizeMB}MB`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Redimensionar imagem (usando canvas)
  static resizeImage(file, maxWidth = 1200, maxHeight = 400, quality = 0.8) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calcular novas dimensões mantendo proporção
        let { width, height } = img;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        // Desenhar imagem redimensionada
        ctx.drawImage(img, 0, 0, width, height);

        // Converter para base64
        const resizedDataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(resizedDataUrl);
      };

      img.src = URL.createObjectURL(file);
    });
  }

  // =============================
  // STATISTICS HELPERS
  // =============================

  // Função para obter estatísticas completas dos banners
  static async getDetailedStats() {
    try {
      const [statsResponse, bannersResponse] = await Promise.all([
        MarketplaceBannersServices.getBannerStats(),
        MarketplaceBannersServices.listBanners({ page_size: 1 })
      ]);
      
      const stats = statsResponse.data;
      const total = bannersResponse.data.count || 0;
      
      // Calcular percentuais
      const activePercentage = total > 0 ? Math.round((stats.active_banners / total) * 100) : 0;
      const averageCTR = stats.average_ctr || 0;
      
      return {
        ...stats,
        total,
        activePercentage,
        inactivePercentage: 100 - activePercentage,
        averageCTRFormatted: `${averageCTR.toFixed(2)}%`,
      };
    } catch (error) {
      console.error('Erro ao obter estatísticas detalhadas:', error);
      throw error;
    }
  }
}

export default MarketplaceBannersServices;